import React from 'react';
import { Helmet } from 'react-helmet';
import { injectIntl } from 'react-intl';

import { Footer } from '../../features/footer/Footer';
import { Header } from '../../features/header/Header';

import { withRouter } from 'react-router-dom';
import messages from './dashboardsMessages';
import { connect } from 'react-redux';
import CustomDashboards from './customDashboards';
import AllDashboards from './allDashboards';
import AssetClass, {
  AssetClassId,
  availableAssetClasses,
} from '../../common/domain/AssetClass';
import FixedDashboards from './fixedDashboards';
import UserPreferences from '../../common/domain/UserPreferences';
import {
  withAuthentication,
  getThoughtSpotToken,
} from '../../common/authentication';
import {
  ConversationEmbed,
  AuthType,
  init,
} from '@thoughtspot/visual-embed-sdk';
import config from '../../config';
import styled from 'styled-components';
import spotterLogo from '../../images/SpotterLogo.webp';
import {
  SPOTTER_ICON_SPRITE_URL,
  SPOTTER_TEXT_CUSTOMIZATION,
} from '../../common/constants';

const SpotterButton = styled.button`
  position: fixed;
  bottom: 1.25rem;
  right: 1.25rem;
  width: 3.75rem;
  height: 3.75rem;
  border-radius: 50%;
  background-color: rgb(7, 17, 32);
  color: white;
  border: none;
  box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.2);
  cursor: pointer;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &:hover {
    background-color: rgb(14, 34, 64);
  }

  img {
    width: 50%;
    height: auto;
  }
`;

const SpotterContainer = styled.div`
  position: fixed;
  bottom: 5.5rem;
  right: 1.25rem;
  width: 40.5rem;
  height: 37.5rem;
  background-color: white;
  border-radius: 0.625rem;
  box-shadow: 0 0.25rem 1.25rem rgba(0, 0, 0, 0.15);
  z-index: 999;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .spotter-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.625rem 0.875rem;
    background-color: #f5f5f5;
    border-bottom: 1px solid #e0e0e0;
  }

  .spotter-header h4 {
    margin: 0;
    font-size: 1rem;
  }

  .close-button {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 1rem;
  }

  .spotter-content {
    flex: 1;
    overflow: hidden;
  }
`;

export interface DashboardsPageProps {
  intl: any;
  dashboards: Array<any>;
  // The store's preferences, with its load marker and requests in flight (ANS-1118).
  preferences: UserPreferences & { loaded?: object; requests?: { loading?: boolean } };
  status: any;
  getDashboards();
  getPreferences();
  savePreferences(preferences: Partial<UserPreferences>);
  match: any;
  history: any;
  authState?: any;
}

function resolveToAssetClass(id?: AssetClassId): AssetClass | undefined {
  if (id == null) return undefined;
  return availableAssetClasses(config.dashboards).filter((ac) => ac.id === id)[0];
}

interface DashboardsPageState {
  spotterVisible: boolean;
}

export class DashboardsPage extends React.Component<
  DashboardsPageProps,
  DashboardsPageState
> {
  private spotterContainerRef: React.RefObject<HTMLDivElement>;
  private conversation: ConversationEmbed | null = null;
  // The path's asset class at the last update: the page asks and saves only when it changes.
  private pathAssetClassId?: AssetClassId;

  constructor(props: DashboardsPageProps) {
    super(props);
    this.state = {
      spotterVisible: false,
    };
    this.spotterContainerRef = React.createRef();
    this.toggleSpotter = this.toggleSpotter.bind(this);
  }

  componentDidMount() {
    this.props.getDashboards();
    // With no asset class in the path, the redirect waits for this load (componentDidUpdate).
    if (!resolveToAssetClass(this.props.match?.params?.assetclassId)) {
      this.props.getPreferences();
    }

    // Jitto Answer has no AI Assistant; with it on, nothing here asks ThoughtSpot.
    if (config.jittoEmbedActiveFlag) return;

    // Initialize ThoughtSpot SDK with customizations
    init({
      thoughtSpotHost: config.thoughtSpotHost,
      authType: AuthType.AuthServer,
      username: this.props.authState?.username,
      // Add customizations for text strings and styling
      customizations: {
        iconSpriteUrl: SPOTTER_ICON_SPRITE_URL,
        content: {
          strings: SPOTTER_TEXT_CUSTOMIZATION,
        },
        style: {
          customCSS: {
            rules_UNSTABLE: {
              '.landing-page-module__landingAiIcon': {
                display: 'none',
              },
              '.chat-module__chatItemsWrapperContainer': {
                padding: '4.375rem',
              },
              '.chat-module__chatContainerWrapper': {
                'min-width': '100%',
              },
              '.chat-input-module__chatInputContainer': {
                'min-width': '100%',
              },
              '.authenticated-app-view-module__pageContent': {
                overflow: 'hidden !important',
              },
              '.chat-input-module__chatInputContainer:before': {
                'backdrop-filter': 'none !important',
              },
            },
          },
        },
      },
      getAuthToken: () =>
        new Promise((resolve, reject) => {
          getThoughtSpotToken(this.props.authState?.accessToken).then(
            (tsinfo: any) => {
              resolve(tsinfo.thoughtSpotToken);
            },
          );
        }),
    });
  }

  // On /dashboards with no asset class, once the preferences load: the one in
  // them, or the first this environment has when there is none or they fail to
  // load. Without it a failed preferences call leaves the cards loading forever.
  openDefaultAssetClass() {
    const { loaded, requests, assetClass } = this.props.preferences || {};
    if (!loaded || requests?.loading) return;
    const target =
      resolveToAssetClass(assetClass) || availableAssetClasses(config.dashboards)[0];
    if (target) this.props.history.replace(`/dashboards/${target.id}`);
  }

  componentDidUpdate(
    prevProps: Readonly<DashboardsPageProps>,
    prevState: Readonly<DashboardsPageState>,
  ): void {
    const pathAssetClass = resolveToAssetClass(
      this.props.match?.params?.assetclassId,
    );

    // A preferences/get updates the store at once (ANS-1118): asking on every update loops.
    const pathChanged = pathAssetClass?.id !== this.pathAssetClassId;
    this.pathAssetClassId = pathAssetClass?.id;

    if (pathAssetClass == null && pathChanged) {
      this.props.getPreferences();
    }
    if (pathAssetClass == null) {
      this.openDefaultAssetClass();
    } else if (pathAssetClass != null && pathChanged) {
      this.props.savePreferences({ assetClass: pathAssetClass.id });
    }

    // Initialize Spotter when it becomes visible
    if (this.state.spotterVisible && !prevState.spotterVisible) {
      this.initializeSpotter();
    }

    // Re-initialize Spotter when asset class changes
    if (
      this.state.spotterVisible &&
      prevProps.match?.params?.assetclassId !==
        this.props.match?.params?.assetclassId
    ) {
      this.initializeSpotter();
    }
  }

  toggleSpotter() {
    const newVisibleState = !this.state.spotterVisible;

    this.setState(
      {
        spotterVisible: newVisibleState,
      },
      () => {
        // Initialize the spotter immediately if it's being made visible
        if (newVisibleState) {
          this.initializeSpotter();
        }
      },
    );
  }

  initializeSpotter() {
    if (!this.spotterContainerRef.current) {
      return;
    }

    // Get asset class ID from URL params, or use the value from props.preferences
    let assetClassId = this.props.match?.params?.assetclassId;

    // If there's no asset class in URL, try to use the one from preferences
    if (!assetClassId && this.props.preferences?.assetClass) {
      assetClassId = this.props.preferences.assetClass;
    }

    if (!assetClassId) {
      return;
    }

    const assetClassConfig = config.dashboards[assetClassId];
    if (!assetClassConfig) {
      return;
    }

    // Try to get worksheetId (check both property names to be safe)
    let worksheetId =
      assetClassConfig.worksheetId || assetClassConfig.datasource;

    if (!worksheetId) {
      return;
    }

    // Destroy previous instance if it exists
    if (this.conversation) {
      this.conversation.destroy();
    }

    // Create and render the ConversationEmbed component
    this.conversation = new ConversationEmbed(
      this.spotterContainerRef.current,
      {
        frameParams: {
          width: '100%',
          height: '100%',
        },
        worksheetId: worksheetId,
        disableSourceSelection: true,
        hideSampleQuestions: true,
      },
    );

    this.conversation.render();
  }

  render() {
    const { intl } = this.props;
    const { spotterVisible } = this.state;

    return (
      <>
        <Helmet>
          <title>{intl.formatMessage(messages.title)}</title>
          <meta name="description" content="Description of DashboardsPage" />
        </Helmet>
        <Header />
        <FixedDashboards
          assetClass={resolveToAssetClass(
            this.props.match?.params?.assetclassId,
          )}
        />
        {config.jittoEmbedActiveFlag ? (
          <AllDashboards
            dashboards={this.props?.dashboards || []}
            status={this.props.status}
          />
        ) : (
          <CustomDashboards
            dashboards={this.props?.dashboards || []}
            status={this.props.status}
          />
        )}
        <Footer />

        {/* Spotter Button: ThoughtSpot's, so not with Jitto Answer */}
        {!config.jittoEmbedActiveFlag && (
          <SpotterButton onClick={this.toggleSpotter} title="AI Assistant">
            <img src={spotterLogo} alt="AI Assistant" />
          </SpotterButton>
        )}

        {/* Spotter Container */}
        {!config.jittoEmbedActiveFlag && spotterVisible && (
          <SpotterContainer>
            <div className="spotter-header">
              <h4>Trading Analytics AI Assistant</h4>
              <button className="close-button" onClick={this.toggleSpotter}>
                <i className="fa fa-times"></i>
              </button>
            </div>
            <div
              className="spotter-content"
              ref={this.spotterContainerRef}
            ></div>
          </SpotterContainer>
        )}
      </>
    );
  }
}

export default connect(
  (globalState: any) => ({ preferences: globalState.preferences }),
  {
    getPreferences: () => ({
      type: 'preferences/get',
    }),
    savePreferences: (preferences: Partial<UserPreferences>) => ({
      type: 'preferences/save',
      payload: preferences,
    }),
  },
)(
  connect((globalState: any) => globalState.dashboards, {
    getDashboards: () => ({
      type: 'dashboards/getdashboards',
    }),
  })(withAuthentication(withRouter(injectIntl(DashboardsPage as any)))),
);
